import { faPlus, faTableColumns } from "@fortawesome/pro-regular-svg-icons";

import { FlowVersionFlowChild } from "src/api/flowTypes";
import { DatasetColumnGroup, GLOBAL_FEATURES_TYPE } from "src/api/types";
import { useAvailableIntegrationNodes } from "src/datasets/DatasetTable/hooks";
import { DatasetContext } from "src/datasets/DatasetTable/types";
import { useSchemas } from "src/datasets/utils";
import {
  ActionMenuItemProps,
  EmptyState as BaseEmptyState,
} from "src/design-system/EmptyState";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";

export const EmptyState: React.FC<{
  version?: FlowVersionFlowChild;
  onAddColumn: (name: string, group: DatasetColumnGroup) => void;
  onAddAdditionalColumn: () => void;
  context: DatasetContext;
}> = ({ version, onAddColumn, onAddAdditionalColumn, context }) => {
  const integrationNodes = useAvailableIntegrationNodes(version);
  const featureGates = useWorkspaceFeatureGates();
  const schemas = useSchemas(version);

  const isAuthoringDatasetContext = context === "authoring";

  const inputColumns = [
    ...(schemas.input?.properties?.map((prop) => prop.fieldName) ?? []),
    ...(featureGates.featuresEventsEnabled ? [GLOBAL_FEATURES_TYPE] : []),
  ];
  const mockColumns = isAuthoringDatasetContext
    ? integrationNodes.map((node) => node.name)
    : [];
  const outputColumns = isAuthoringDatasetContext
    ? (schemas.output?.properties?.map((prop) => prop.fieldName) ?? [])
    : [];

  const columnGroup = (
    label: string,
    group: DatasetColumnGroup,
    columns: string[],
  ): ActionMenuItemProps[] =>
    columns.length > 0
      ? [
          {
            label,
            icon: faPlus,
            items: columns.map((column) => ({
              label: column,
              icon: faPlus,
              onClick: () => onAddColumn(column, group),
            })),
          },
        ]
      : [];

  return (
    <BaseEmptyState
      actionButton={{
        label: "Add column",
        icon: faPlus,
        items: [
          ...columnGroup("Add input column", "input_columns", inputColumns),
          ...columnGroup("Add mock column", "mock_columns", mockColumns),
          ...columnGroup(
            "Add expected column",
            "output_columns",
            outputColumns,
          ),
          {
            label: "Add additional column",
            icon: faPlus,
            onClick: onAddAdditionalColumn,
          },
        ],
      }}
      description="Add a column to this dataset"
      headline="Add Column"
      icon={faTableColumns}
    />
  );
};
